import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { type Contact, type MatterContactConnection } from "@/lib/types";
import { useEffect, useRef, useState } from "react";
import {
  EditContactForm,
  type EditContactFormHandle,
  type EditContactFormProps,
} from "./EditContactForm";

export const EDIT_CONTACT_MODAL_FORM_ID = "edit-contact-modal-form";

interface EditContactModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  creatingMore?: boolean;
  activeContactId?: string;
  activeContact?: Contact;
  hideSubItems?: boolean;
  hasEditPermission?: boolean;
  handleSubmit?: (contact: Contact) => void;
  /** Optional: enriches the left preview (role on matter) like edit contact connection. */
  matterContactConnection?: MatterContactConnection;
  matterId?: string;
  formId?: string;
}

export const EditContactModal = ({
  open,
  setOpen,
  activeContactId,
  activeContact,
  hideSubItems,
  hasEditPermission,
  handleSubmit,
  matterContactConnection,
  matterId,
  formId: formIdProp,
}: EditContactModalProps) => {
  const [mutationPending, setMutationPending] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const resolvedFormId = formIdProp ?? EDIT_CONTACT_MODAL_FORM_ID;
  const editFormRef = useRef<EditContactFormHandle>(null);

  useEffect(() => {
    if (!open) {
      setMutationPending(false);
      setIsDirty(false);
    }
  }, [open]);

  const formProps: EditContactFormProps = {
    open,
    setOpen,
    activeContactId,
    activeContact,
    hideSubItems,
    hasEditPermission,
    handleSubmit,
    onMutationPendingChange: setMutationPending,
    onDirtyChange: setIsDirty,
    hideFooter: true,
    formId: resolvedFormId,
    livePreviewLayout: true,
    matterContactConnection,
    matterId,
  };

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title="Update contact details"
      size="3xl"
      loading={mutationPending}
      isDirty={isDirty}
      overlayClassName="z-[108]"
      className="z-[109] h-[min(80vh,780px)] w-[calc(100vw-2rem)] max-w-5xl"
      bodyClassName="min-h-0 px-1"
      bodyContained
      footer={
        <div className="flex w-full flex-wrap justify-end gap-2">
          <FormCancelButton setOpen={setOpen} isDirty={isDirty} />
          <Button
            type="button"
            disabled={
              mutationPending || !isDirty || hasEditPermission === false
            }
            onClick={() => {
              void editFormRef.current?.submit();
            }}
          >
            {mutationPending ? <LoadingSpinner /> : "Save"}
          </Button>
        </div>
      }
    >
      {/* Same flex shell as create contact in `ContactPickerShell` (modal). */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <EditContactForm
            ref={editFormRef}
            key={`${activeContactId ?? "contact"}-${open ? "open" : "closed"}`}
            {...formProps}
          />
        </div>
      </div>
    </AppModal>
  );
};
